import React, { useMemo } from 'react';
import type { TimeRange } from '../../types/dashboard';
import { fmtMoney } from '../../utils/formatters';
import { getTradePnl, normalizeTradeStatus } from '../../lib/trades/authoritativeTradeData';
import { filterTradesForDashboardRange } from '../../lib/trades/dashboardBucketAnalytics';

interface LargestLossWidgetProps {
  dailyData?: any[];
  trades?: any[];
  timeRange?: TimeRange;
}

const LargestLossWidget: React.FC<LargestLossWidgetProps> = ({ trades = [], timeRange = 'month' }) => {
  const { hasClosedTrades, largestLoss } = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) {
      return { hasClosedTrades: false, largestLoss: 0 };
    }
    const activeTrades = filterTradesForDashboardRange(trades as Record<string, any>[], timeRange as any);
    const closedPnls = activeTrades
      .filter((t) => normalizeTradeStatus(t) !== 'open')
      .map((t) => getTradePnl(t))
      .filter((pnl) => Number.isFinite(pnl));

    if (closedPnls.length === 0) {
      return { hasClosedTrades: false, largestLoss: 0 };
    }

    const losses = closedPnls.filter((pnl) => pnl < 0);
    return {
      hasClosedTrades: true,
      largestLoss: losses.length > 0 ? Math.min(...losses) : 0,
    };
  }, [trades, timeRange]);

  return (
    <div className="h-full w-full flex flex-col justify-center items-center p-4">
      {hasClosedTrades ? (
        <div className="w-full text-center" data-testid="largest-loss-amount">
          <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-red-500">{fmtMoney(largestLoss)}</span>
        </div>
      ) : (
        <div className="text-sm text-zinc-500 dark:text-zinc-400">Import Trades to see the widget</div>
      )}
    </div>
  );
};

export default LargestLossWidget;
export { LargestLossWidget };